// Bill Pay & Recharge API (spec §2). Mock-first; the real Bharat Connect
// (BBPOU) integration replaces these when the backend ships (spec §13.3).
import { ApiError, createIdempotencyKey, del, get, post, put } from './client';
import { MOCK_BILL_PAY, REAL_API_ENABLED } from './flags';
import * as mock from '@/mock/handlers/billPay';
import type {
  BillFetch,
  BillPayment,
  BillComplaint,
  Mandate,
  SavedBiller,
} from '@/types/phase4.types';

const unimplemented = (): never => {
  throw new Error('Bill Pay backend not available yet. This build runs on the mock (VITE_MOCK_BILL_PAY).');
};

const unsupportedLegacyRoute = (route: string): never => {
  throw new ApiError('NOT_SUPPORTED', `${route} is not supported by the live API. Use the /bill-pay route instead.`);
};

export const fetchBill = (billerId: string, params: Record<string, string>): Promise<BillFetch> =>
  MOCK_BILL_PAY ? mock.fetchBill(billerId, params) : unimplemented();

export const pay = (input: {
  billerId: string;
  params: Record<string, string>;
  amountRupees: number;
  nickname?: string;
  autopay?: boolean;
}): Promise<BillPayment> => (MOCK_BILL_PAY ? mock.pay(input) : unimplemented());

export interface PayFromCardInput {
  [key: string]: unknown;
}

export const payBillFromCard = (
  input: PayFromCardInput,
  idempotencyKey = createIdempotencyKey(),
): Promise<Record<string, unknown>> =>
  REAL_API_ENABLED
    ? post<Record<string, unknown>>('/bill-pay/pay-from-card', input, {
        headers: { 'Idempotency-Key': idempotencyKey },
      })
    : unimplemented();

export interface BillControllerPaymentInput {
  billerCode: string;
  consumerNumber: string;
  amountPaise: number;
  debitAccountNumber: string;
}

export interface BillControllerFetchInput {
  billerCode: string;
  consumerNumber: string;
}

export type BillControllerRecord = Record<string, unknown>;
export type BillControllerList = BillControllerRecord[];

const billPaymentPath = (id: string): string =>
  `/bill-pay/payments/${encodeURIComponent(id)}`;

export const payBill = (
  input: BillControllerPaymentInput,
  idempotencyKey = createIdempotencyKey(),
): Promise<BillControllerRecord> =>
  REAL_API_ENABLED
    ? post<BillControllerRecord>('/bill-pay/pay', input, {
        headers: { 'Idempotency-Key': idempotencyKey },
      })
    : unimplemented();

/** @deprecated The legacy /bills/pay route is not called against the live API. */
export const payBillLegacy = (
  _input: BillControllerPaymentInput,
  _idempotencyKey?: string,
): Promise<BillControllerRecord> => unsupportedLegacyRoute('/bills/pay');

export const fetchBillDetails = (
  input: BillControllerFetchInput,
): Promise<BillControllerRecord> =>
  REAL_API_ENABLED
    ? post<BillControllerRecord>('/bill-pay/fetch', input)
    : unimplemented();

/** @deprecated The legacy /bills/fetch route is not called against the live API. */
export const fetchBillDetailsLegacy = (
  _input: BillControllerFetchInput,
): Promise<BillControllerRecord> => unsupportedLegacyRoute('/bills/fetch');

export const getBillPaymentReceipt = (id: string): Promise<BillControllerRecord> =>
  REAL_API_ENABLED
    ? get<BillControllerRecord>(`${billPaymentPath(id)}/receipt`)
    : unimplemented();

/** @deprecated The legacy /bills/payments route is not called against the live API. */
export const getBillPaymentReceiptLegacy = (_id: string): Promise<BillControllerRecord> =>
  unsupportedLegacyRoute('/bills/payments/{id}/receipt');

export const getBillPayment = (id: string): Promise<BillControllerRecord> =>
  REAL_API_ENABLED ? get<BillControllerRecord>(billPaymentPath(id)) : unimplemented();

/** @deprecated The legacy /bills/payments route is not called against the live API. */
export const getBillPaymentLegacy = (_id: string): Promise<BillControllerRecord> =>
  unsupportedLegacyRoute('/bills/payments/{id}');

export const listBillPayments = (): Promise<BillControllerList> =>
  REAL_API_ENABLED ? get<BillControllerList>('/bill-pay/payments') : unimplemented();

/** @deprecated The legacy /bills/payments route is not called against the live API. */
export const listBillPaymentsLegacy = (): Promise<BillControllerList> =>
  unsupportedLegacyRoute('/bills/payments');

export const listBillCategories = (): Promise<string[]> =>
  REAL_API_ENABLED ? get<string[]>('/bill-pay/categories') : unimplemented();

export interface BillControllerBiller {
  id: string;
  code: string;
  name: string;
  category: string;
  active: boolean;
}

export const listBillers = (category?: string): Promise<BillControllerBiller[]> =>
  REAL_API_ENABLED
    ? get<BillControllerBiller[]>('/bill-pay/billers', {
        params: category ? { category } : undefined,
      })
    : unimplemented();

export const payments = (): Promise<BillPayment[]> => (MOCK_BILL_PAY ? mock.payments() : unimplemented());

export const savedBillers = (): Promise<SavedBiller[]> =>
  MOCK_BILL_PAY
    ? mock.savedBillers()
    : REAL_API_ENABLED
      ? get<SavedBiller[]>('/bill-pay/saved')
      : unimplemented();

export interface SavedBillerUpdate {
  nickname?: string;
  debitAccountNumber?: string;
}

export type BillPayMutationResult = Record<string, unknown>;

const updateSavedBillerApi = (
  path: string,
  id: string,
  input: SavedBillerUpdate,
): Promise<void | BillPayMutationResult> =>
  put<BillPayMutationResult>(`${path}/${encodeURIComponent(id)}`, undefined, {
    params: input,
  });

export const updateSavedBiller = (
  id: string,
  input: SavedBillerUpdate,
): Promise<void | BillPayMutationResult> =>
  REAL_API_ENABLED
    ? updateSavedBillerApi('/bill-pay/saved', id, input)
    : MOCK_BILL_PAY
      ? mock.renameBiller(id, input.nickname ?? '')
      : unimplemented();

export const updateBiller = (
  id: string,
  input: SavedBillerUpdate,
): Promise<void | BillPayMutationResult> =>
  REAL_API_ENABLED
    ? updateSavedBillerApi('/bill-pay/billers', id, input)
    : MOCK_BILL_PAY
      ? mock.renameBiller(id, input.nickname ?? '')
      : unimplemented();

export interface SaveBillerInput {
  billerId: string;
  params: Record<string, string>;
  nickname: string;
}

export const saveBiller = (input: SaveBillerInput): Promise<SavedBiller> =>
  MOCK_BILL_PAY
    ? mock.saveBiller(input)
    : REAL_API_ENABLED
      ? post<SavedBiller>('/bill-pay/saved', input)
      : unimplemented();

export const renameBiller = (id: string, nickname: string): Promise<void> =>
  MOCK_BILL_PAY ? mock.renameBiller(id, nickname) : unimplemented();

export const deleteBiller = (id: string): Promise<void | BillPayMutationResult> =>
  MOCK_BILL_PAY
    ? mock.deleteBiller(id)
    : REAL_API_ENABLED
      ? del<BillPayMutationResult>(`/bill-pay/saved/${encodeURIComponent(id)}`)
      : unimplemented();

export const mandates = (): Promise<Mandate[]> =>
  MOCK_BILL_PAY
    ? mock.mandates()
    : REAL_API_ENABLED
      ? get<Mandate[]>('/bill-pay/mandates')
      : unimplemented();

export type CreateMandateInput = Omit<Mandate, 'id' | 'paused' | 'skipNext' | 'createdAt'>;

export const createMandate = (input: CreateMandateInput): Promise<Mandate> =>
  MOCK_BILL_PAY
    ? mock.createMandate(input)
    : REAL_API_ENABLED
      ? post<Mandate>('/bill-pay/mandates', input)
      : unimplemented();

// The live API does not expose PUT /bill-pay/mandates/{id}. Use the
// dedicated pause/skip/hold/approve operations below instead.
export const updateMandate = (id: string, patch: Partial<Mandate>): Promise<void> => {
  if (MOCK_BILL_PAY) return mock.updateMandate(id, patch);
  return Promise.reject(new ApiError('NOT_SUPPORTED', 'Mandates must be changed using their dedicated actions.'));
};

export const setMandatePaused = (id: string, paused: boolean): Promise<void | BillPayMutationResult> =>
  MOCK_BILL_PAY
    ? mock.setMandatePaused(id, paused)
    : REAL_API_ENABLED
      ? put<BillPayMutationResult>(`/bill-pay/mandates/${encodeURIComponent(id)}/pause`, undefined, {
          params: { paused },
        })
      : unimplemented();

export const deleteMandate = (id: string): Promise<void | BillPayMutationResult> =>
  MOCK_BILL_PAY
    ? mock.deleteMandate(id)
    : REAL_API_ENABLED
      ? del<BillPayMutationResult>(`/bill-pay/mandates/${encodeURIComponent(id)}`)
      : unimplemented();

export const skipNextDebit = (id: string): Promise<void | BillPayMutationResult> =>
  MOCK_BILL_PAY
    ? mock.skipNextDebit(id)
    : REAL_API_ENABLED
      ? post<BillPayMutationResult>(`/bill-pay/mandates/${encodeURIComponent(id)}/skip-next`)
      : unimplemented();

export const holdMandateDebit = (
  id: string,
  input: Record<string, unknown>,
): Promise<void | BillPayMutationResult> =>
  MOCK_BILL_PAY
    ? mock.holdMandateDebit(id)
    : REAL_API_ENABLED
      ? post<BillPayMutationResult>(`/bill-pay/mandates/${encodeURIComponent(id)}/hold`, input)
      : unimplemented();

export const approveMandateDebit = (
  id: string,
  idempotencyKey = createIdempotencyKey(),
): Promise<void | BillPayMutationResult> =>
  MOCK_BILL_PAY
    ? mock.approveMandateDebit(id)
    : REAL_API_ENABLED
      ? post<BillPayMutationResult>(
          `/bill-pay/mandates/${encodeURIComponent(id)}/approve-debit`,
          undefined,
          { headers: { 'Idempotency-Key': idempotencyKey } },
        )
      : unimplemented();

export const complaints = (): Promise<BillComplaint[]> =>
  MOCK_BILL_PAY
    ? mock.complaints()
    : REAL_API_ENABLED
      ? get<BillComplaint[]>('/bill-pay/complaints')
      : unimplemented();

export const raiseComplaint = (input: {
  kind: 'TRANSACTION' | 'SERVICE';
  target: string;
  reason: string;
  description: string;
}): Promise<BillComplaint> => {
  if (MOCK_BILL_PAY) return mock.raiseComplaint(input);
  return Promise.reject(new ApiError('NOT_SUPPORTED', 'Bill-pay complaint creation is not available from the live API yet.'));
};
